import {Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {mergeAll} from 'ramda';

import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {DmsDataGrid, Section, UploadDocumentsDialog, refreshDmsDatagrid} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

interface DocumentsProps extends RequiredTestIdProps {
  onDocumentAction: VoidFunction;
}

export function Documents(props: DocumentsProps) {
  const {costCorrectionId} = useWarehouseParams();

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const [rowActions] = useGetDocumentDatagridActions({
    contextId: costCorrectionId,
    contextTarget: 'warehouse-issue-note-correction',
    refreshData: () => refreshDmsDatagrid(DOCUMENTS_GRID_CODE),
  });
  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([filter, {warehouseIssueNoteCorrectionId: costCorrectionId}]),
    [costCorrectionId]
  );

  return (
    <Section data-testid={suffixTestId('wrapper', props)}>
      <Card
        title={i18n.t('entity.warehouse.labels.documents')}
        actions={[
          {
            type: 'button',
            variant: 'ghostLink',
            leftIcon: 'action/backup',
            title: i18n.t('entity.document.actions.uploadDocument'),
            onClick: openUploadDialog,
            'data-testid': suffixTestId('action.addDocument', props),
          },
        ]}
      >
        <Box height="100%">
          <DmsDataGrid
            gridCode={DOCUMENTS_GRID_CODE}
            actionCallback={rowActions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            autoHeight
            data-testid="documentContextListWarehouseIssueNoteCorrection"
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={() => {
          refreshDmsDatagrid(DOCUMENTS_GRID_CODE);
          props.onDocumentAction();
        }}
        contextTarget="warehouse-issue-note-correction"
        contextId={costCorrectionId}
        bulkContext={[{target: 'warehouse-issue-note-correction', targetId: costCorrectionId}]}
        data-testid={suffixTestId('upload-document', props)}
      />
    </Section>
  );
}

const DOCUMENTS_GRID_CODE = 'document-context-list-warehouse-issue-note-correction';
